import { API_ENDPOINTS, ApiEndpoint } from './apiCatalog';

/**
 * Coleção do Postman gerada do mesmo catálogo que a documentação da aba: endpoint novo entra
 * nos dois de uma vez, ou em nenhum. Duas listas divergiriam na primeira mudança.
 *
 * A coleção sai em pastas, uma por grupo, espelhando a tela. A autenticação vai por variável,
 * nunca com valor: `integrationToken` (API de parceiro), `tenantId` (X-TENANT-ID) e
 * `sessionToken` (sessão do dashboard) chegam vazias para quem importar preencher no Postman.
 * Coleção com credencial dentro é arquivo que circula por e-mail, entra em repositório e
 * sobrevive à revogação na cabeça de quem baixou.
 */

const SCHEMA = 'https://schema.getpostman.com/json/collection/v2.1.0/collection.json';

type PostmanHeader = { key: string; value: string };
type PostmanQuery = { key: string; value: string; description?: string };
type PostmanPathVar = { key: string; value: string; description?: string };

type PostmanItem = {
  name: string;
  request: {
    method: string;
    header: PostmanHeader[];
    url: {
      raw: string;
      host: string[];
      path: string[];
      query?: PostmanQuery[];
      variable?: PostmanPathVar[];
    };
    body?: { mode: 'raw'; raw: string; options: { raw: { language: 'json' } } };
    description: string;
  };
};

type PostmanFolder = { name: string; item: PostmanItem[] };

const authHeaders = (endpoint: ApiEndpoint): PostmanHeader[] => {
  switch (endpoint.auth) {
    case 'INTEGRATION_TOKEN':
      return [{ key: 'Authorization', value: 'Bearer {{integrationToken}}' }];
    case 'TENANT_ID':
      // X-TENANT-ID vai como variável; a sessão do dashboard também, porque a rota passa pelo gateway.
      return [
        { key: 'X-TENANT-ID', value: '{{tenantId}}' },
        { key: 'Authorization', value: 'Bearer {{sessionToken}}' },
      ];
    case 'SESSION':
      return [{ key: 'Authorization', value: 'Bearer {{sessionToken}}' }];
    default:
      return [];
  }
};

const describe = (endpoint: ApiEndpoint): string => {
  const lines = [endpoint.summary, ''];

  if (endpoint.auth === 'INTEGRATION_TOKEN') {
    lines.push(`Required scopes: ${(endpoint.scopes ?? []).join(', ')}`);
  } else {
    lines.push(`Auth: ${endpoint.auth}`);
  }

  return lines.join('\n');
};

const pathVarsOf = (endpoint: ApiEndpoint): PostmanPathVar[] =>
  (endpoint.pathParams ?? []).map((param) => ({
    key: param.name,
    value: param.example || '',
    description: param.description || undefined,
  }));

const itemOf = (endpoint: ApiEndpoint): PostmanItem => {
  const raw = `{{baseUrl}}${endpoint.path}`;
  const header: PostmanHeader[] = [...authHeaders(endpoint), { key: 'Accept', value: 'application/json' }];

  const item: PostmanItem = {
    name: `${endpoint.method} ${endpoint.path}`,
    request: {
      method: endpoint.method,
      header,
      url: {
        raw,
        host: ['{{baseUrl}}'],
        path: endpoint.path.split('/').filter(Boolean),
        ...(endpoint.query
          ? {
              query: endpoint.query.map((param) => ({
                key: param.name,
                value: param.example || '',
                description: param.description || undefined,
              })),
            }
          : {}),
        ...(endpoint.pathParams ? { variable: pathVarsOf(endpoint) } : {}),
      },
      description: describe(endpoint),
    },
  };

  if (endpoint.bodyExample) {
    item.request.header.push({ key: 'Content-Type', value: 'application/json' });
    item.request.body = {
      mode: 'raw',
      raw: endpoint.bodyExample,
      options: { raw: { language: 'json' } },
    };
  }

  return item;
};

/** Pastas por grupo, na ordem em que os grupos aparecem no catálogo. */
const foldersOf = (endpoints: ApiEndpoint[]): PostmanFolder[] => {
  const order: string[] = [];
  const byGroup = new Map<string, PostmanItem[]>();

  endpoints.forEach((endpoint) => {
    if (!byGroup.has(endpoint.group)) {
      byGroup.set(endpoint.group, []);
      order.push(endpoint.group);
    }
    byGroup.get(endpoint.group)!.push(itemOf(endpoint));
  });

  return order.map((group) => ({ name: group, item: byGroup.get(group)! }));
};

/**
 * A coleção, pronta para virar arquivo. `baseUrl` já vem preenchida com o endereço deste
 * ambiente — a parte que a pessoa não tem como adivinhar. Os tokens vêm vazios de propósito.
 */
export const buildPostmanCollection = (baseUrl: string, accountName: string) => ({
  info: {
    name: `Move — tenant-cross${accountName ? ` (${accountName})` : ''}`,
    description: [
      'Endpoints do tenant-cross: a API de integração (pasta Integration, token de parceiro) e',
      'a API interna (demais pastas, identidade de tenant/sessão).',
      '',
      'Autenticação por variável de ambiente, preenchida por quem importa:',
      '- integrationToken: token de integração (API de parceiro). Exibido uma única vez, na criação.',
      '- tenantId: valor do header X-TENANT-ID.',
      '- sessionToken: token de sessão do dashboard.',
      '',
      'A coleção não carrega token, de propósito.',
    ].join('\n'),
    schema: SCHEMA,
  },
  variable: [
    { key: 'baseUrl', value: baseUrl, type: 'string' },
    { key: 'integrationToken', value: '', type: 'string' },
    { key: 'tenantId', value: '', type: 'string' },
    { key: 'sessionToken', value: '', type: 'string' },
  ],
  item: foldersOf(API_ENDPOINTS),
});

/** Todos os itens da coleção, achatando as pastas. Usado pelos testes e por quem conta cobertura. */
export const flattenItems = (
  collection: ReturnType<typeof buildPostmanCollection>,
): PostmanItem[] => collection.item.flatMap((folder) => folder.item);

/** Nome do arquivo, sem espaço nem acento, para não incomodar em nenhum sistema. */
export const postmanFileName = (accountName: string): string => {
  const slug = (accountName || 'conta')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

  return `move-tenant-cross-${slug}.postman_collection.json`;
};
